import { format } from 'date-fns';

function isEmpty(value) {
    return value === null || value === undefined || value === '';
}

function normalizeValue(value) {
    if (isEmpty(value)) return '-';
    return value;
}

function normalizeSearchValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.toLowerCase();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value).toLowerCase();
    return '';
}

function normalizeSortValue(value) {
    if (isEmpty(value)) return null;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'number' || typeof value === 'boolean') return value;

    if (typeof value === 'string') {
        const parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) return parsedDate.getTime();
        return value.toLowerCase();
    }

    return String(value).toLowerCase();
}

function getCell(row, columnKey) {
    return row?.cells?.[columnKey] || null;
}

function getValueByPriority(cell) {
    if (!cell) return null;
    if (cell.exportValue !== undefined && cell.exportValue !== null) return cell.exportValue;
    if (cell.displayValue !== undefined && cell.displayValue !== null) return cell.displayValue;
    if (cell.value !== undefined && cell.value !== null) return cell.value;
    return null;
}

export function getSidebarTableCellDisplayValue(row, column) {
    const cell = getCell(row, column.key);
    if (!cell) return '-';

    const formatterValue = cell.displayValue !== undefined ? cell.displayValue : cell.value;

    if (typeof column.format === 'function') {
        return normalizeValue(column.format(formatterValue, row, cell));
    }

    return normalizeValue(getValueByPriority(cell));
}

export function getSidebarTableCellSortValue(row, column) {
    const cell = getCell(row, column.key);
    if (!cell) return '';

    if (typeof column.getSortValue === 'function') {
        return column.getSortValue(row, cell);
    }

    if (cell.value !== undefined && cell.value !== null) {
        return cell.value;
    }

    return getValueByPriority(cell);
}

export function getSidebarTableSearchText(row, columns) {
    if (row?.searchableText) return normalizeSearchValue(row.searchableText);

    return columns
        .filter((column) => column.searchable)
        .map((column) => {
            const cell = getCell(row, column.key);
            if (!cell) return '';

            if (typeof column.getSearchValue === 'function') {
                return normalizeSearchValue(column.getSearchValue(row, cell));
            }

            return normalizeSearchValue(getValueByPriority(cell));
        })
        .filter(Boolean)
        .join(' ');
}

export function getSidebarTableSearchableRowText(row, columns) {
    return getSidebarTableSearchText(row, columns);
}

export function compareSidebarTableValues(a, b) {
    if (a === b) return 0;
    if (a === null || a === undefined || a === '') return 1;
    if (b === null || b === undefined || b === '') return -1;

    if (typeof a === 'number' && typeof b === 'number') {
        return a - b;
    }

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
}

export function prepareSidebarTableRows(tableConfig, rows = [], query = '', activeSort = null) {
    const { columns = [], search = {}, sort = {} } = tableConfig || {};
    const { enabled: searchEnabled = true } = search;

    const searchableQuery = normalizeSearchValue(query).trim();
    const resolvedSort = activeSort || sort || {};

    let nextRows = rows;

    if (searchEnabled && searchableQuery) {
        nextRows = nextRows.filter((row) => getSidebarTableSearchText(row, columns).includes(searchableQuery));
    }

    if (resolvedSort?.key) {
        const sortColumn = columns.find((column) => column.key === resolvedSort.key);

        if (sortColumn?.sortable) {
            nextRows = [].concat(nextRows).sort((rowA, rowB) => {
                const valueA = getSidebarTableCellSortValue(rowA, sortColumn);
                const valueB = getSidebarTableCellSortValue(rowB, sortColumn);
                const comparison = compareSidebarTableValues(valueA, valueB);

                return resolvedSort.direction === 'desc' ? comparison * -1 : comparison;
            });
        }
    }

    return nextRows;
}

export function preparePopupTableRows(tableConfig, rows = [], activeSort = null) {
    const { columns = [], sort = {} } = tableConfig || {};
    const resolvedSort = activeSort || sort || {};

    let nextRows = rows;

    if (resolvedSort?.key) {
        const sortColumn = columns.find((column) => column.key === resolvedSort.key);

        if (sortColumn?.sortable) {
            nextRows = [].concat(nextRows).sort((rowA, rowB) => {
                const valueA = typeof sortColumn.getSortValue === 'function'
                    ? sortColumn.getSortValue(rowA)
                    : rowA?.[sortColumn.key];

                const valueB = typeof sortColumn.getSortValue === 'function'
                    ? sortColumn.getSortValue(rowB)
                    : rowB?.[sortColumn.key];

                const comparison = compareSidebarTableValues(normalizeSortValue(valueA), normalizeSortValue(valueB));

                return resolvedSort.direction === 'desc' ? comparison * -1 : comparison;
            });
        }
    }

    return nextRows;
}

function getExportColumns(tableConfig) {
    const columns = tableConfig?.columns || [];

    return columns.filter((column) => {
        if (column.exportable === false) return false;
        if (column.skipExport === true) return false;
        if (!column.key) return false;
        return true;
    });
}

function getColumnExportLabel(column) {
    if (column.exportLabel !== undefined && column.exportLabel !== null) return String(column.exportLabel);
    if (typeof column.label === 'string' || typeof column.label === 'number') return String(column.label);
    return column.key;
}

function getSidebarExportValue(row, column) {
    const cell = getCell(row, column.key);

    if (typeof column.getExportValue === 'function') {
        return column.getExportValue(row, cell);
    }

    if (cell?.exportValue !== undefined) {
        return cell.exportValue;
    }

    return getSidebarTableCellDisplayValue(row, column);
}

function getPopupExportValue(row, column) {
    if (typeof column.getExportValue === 'function') {
        return column.getExportValue(row, column);
    }

    if (typeof column.exportValue === 'function') {
        return column.exportValue(row, column);
    }

    if (typeof column.renderExportValue === 'function') {
        return column.renderExportValue({ row, column });
    }

    const value = row?.[column.key];

    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === null || value === undefined) return '';

    return value;
}

function escapeCsvValue(value, delimiter) {
    const normalized = value === null || value === undefined ? '' : String(value);
    const shouldEscape = normalized.includes(delimiter) || normalized.includes('"') || normalized.includes('\n') || normalized.includes('\r');
    const escaped = normalized.replace(/"/g, '""');

    return shouldEscape ? '"' + escaped + '"' : escaped;
}

function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function sanitizeFilename(value) {
    return String(value || 'export')
        .trim()
        .replace(/[\\/:*?"<>|]+/g, '-')
        .replace(/\s+/g, '-')
        .toLowerCase();
}

function downloadBlob(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    window.setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 0);
}

function exportRowsToFile({ exportColumns, preparedRows, getExportValue, filename, format: exportFormat = 'csv', delimiter = ';' }) {
    const safeFilename = sanitizeFilename(filename);

    if (exportFormat === 'excel') {
        const headerHtml = exportColumns.map((column) => '<th>' + escapeHtml(getColumnExportLabel(column)) + '</th>').join('');
        const rowsHtml = preparedRows.map((row) => {
            const cells = exportColumns.map((column) => '<td>' + escapeHtml(getExportValue(row, column)) + '</td>').join('');
            return '<tr>' + cells + '</tr>';
        }).join('');

        const html = '\ufeff<html><head><meta charset="UTF-8" /></head><body><table><thead><tr>' + headerHtml + '</tr></thead><tbody>' + rowsHtml + '</tbody></table></body></html>';

        downloadBlob(html, safeFilename + '.xls', 'application/vnd.ms-excel;charset=utf-8;');
        return;
    }

    const header = exportColumns.map((column) => escapeCsvValue(getColumnExportLabel(column), delimiter)).join(delimiter);
    const body = preparedRows.map((row) => {
        return exportColumns.map((column) => escapeCsvValue(getExportValue(row, column), delimiter)).join(delimiter);
    }).join('\r\n');

    downloadBlob('\ufeff' + header + '\r\n' + body, safeFilename + '.csv', 'text/csv;charset=utf-8;');
}

export function exportSidebarTable(options) {
    const {
        tableConfig,
        rows = [],
        query = '',
        sort = null,
        filename = 'export',
        format: exportFormat = 'csv',
        delimiter = ';'
    } = options || {};

    const exportColumns = getExportColumns(tableConfig);
    const preparedRows = prepareSidebarTableRows(tableConfig, rows, query, sort);

    exportRowsToFile({
        exportColumns,
        preparedRows,
        getExportValue: getSidebarExportValue,
        filename,
        format: exportFormat,
        delimiter
    });
}

export function exportPopupTable(options) {
    const {
        tableConfig,
        rows = [],
        sort = null,
        filename = 'export',
        format: exportFormat = 'csv',
        delimiter = ';'
    } = options || {};

    const exportColumns = getExportColumns(tableConfig);
    const preparedRows = preparePopupTableRows(tableConfig, rows, sort);

    exportRowsToFile({
        exportColumns,
        preparedRows,
        getExportValue: getPopupExportValue,
        filename,
        format: exportFormat,
        delimiter
    });
}

export function buildExportFilename(prefix, ts, pattern = 'yy-MM-dd') {
    if (!prefix) return '';

    if (!ts) return prefix;

    const date = new Date(ts);
    if (Number.isNaN(date.getTime())) return prefix;

    return prefix + '-' + format(date, pattern);
}